// src/context/authContext.js
//
// Gerencia signIn/signOut/session.
// - Importa sb (supabaseClient) e tenant (tenantContext)
// - Hidrata o tenant após signIn bem-sucedido
// - Limpa o tenant via tenant.clear() no signOut
// - Escuta sb.auth.onAuthStateChange para reagir a refresh de token e logout em outra aba

import { sb } from '../api/supabaseClient.js';
import { tenant } from './tenantContext.js';
import { eventBus } from './eventBus.js';

let _onAuthStateUnsub = null;
let _hydratedUserId = null;   // dedupe da hidratação no boot (por usuário)
let _hydrateInflight = null;  // promise em andamento — evita queries duplicadas

/**
 * Login com email/senha.
 * Após sucesso, busca users_profile + empresas e hidrata o tenant.
 *
 * @returns {Promise<{ ok: boolean, code?: string, message?: string }>}
 */
export async function signIn({ email, password }) {
  if (!email || !password) {
    return { ok: false, code: 'MISSING_CREDS', message: 'Email e senha obrigatórios.' };
  }

  const { data, error } = await sb.auth.signInWithPassword({ email, password });
  if (error) {
    return { ok: false, code: 'AUTH_FAILED', message: error.message };
  }
  if (!data?.user) {
    return { ok: false, code: 'NO_USER', message: 'Resposta sem usuário.' };
  }

  await _hydrateFromUser(data.user);
  eventBus.emit('auth:signedin', { userId: data.user.id });
  return { ok: true };
}

/**
 * Logout · limpa session do Supabase E o tenant context inteiro.
 * Após este chamado, NENHUM cache do tenant anterior deve sobreviver.
 */
export async function signOut() {
  try {
    await sb.auth.signOut();
  } catch (err) {
    console.warn('[authContext] sb.auth.signOut falhou (limpando local mesmo assim):', err);
  }
  tenant.clear(); // emite 'auth:signedout'
  // 🔒 Auditoria pré-prod 03/06: caches módulo-level (window._crCache, _orcCache,
  // _ctCache, ...16 ao todo) sobrevivem ao clear() — em máquina compartilhada de
  // canteiro, dados do tenant ficariam legíveis via console após o logout.
  // Recarga total é a única garantia de memória zerada.
  // ⚠️ location.replace(location.pathname) NAO recarrega quando ha hash: sair de
  // '/#dashboard' para '/' difere so no FRAGMENTO -> navegacao same-document.
  // A "recarga total" prometida acima nunca acontecia, e sem ela o unmount async do
  // shell apagava a tela de login recem-montada (tela preta apos o Sair, 12/07).
  try { history.replaceState(null, '', location.pathname); } catch (_) {}
  location.reload();
}

/**
 * Inicialização · chamada no boot do app.
 * Se já há sessão (auth persistida), hidrata o tenant automaticamente.
 * Registra listener para onAuthStateChange.
 */
export async function initAuth() {
  if (_onAuthStateUnsub) return; // idempotente

  // 1) Listener — reage a TOKEN_REFRESHED, SIGNED_OUT (outra aba), USER_UPDATED
  const { data } = sb.auth.onAuthStateChange(async (event, session) => {
    if (event === 'SIGNED_OUT' || !session) {
      tenant.clear();
      return;
    }
    if (event === 'SIGNED_IN' && session.user && !tenant.isHydrated) {
      await _hydrateFromUser(session.user);
    }
    // TOKEN_REFRESHED · não precisa rehidratar tenant; user_id é estável
  });
  _onAuthStateUnsub = () => data.subscription.unsubscribe();

  // 2) Hidratar se já tiver sessão persistida
  const { data: { session } } = await sb.auth.getSession();
  if (session?.user) {
    await _hydrateFromUser(session.user);
  }
}

/**
 * Limpa o listener (usado em teardown de testes / hot-reload do Vite).
 */
export function teardownAuth() {
  _onAuthStateUnsub?.();
  _onAuthStateUnsub = null;
}

// ── interno ─────────────────────────────────────────────────────────

async function _hydrateFromUser(user) {
  // Dedupe: no boot, o listener (SIGNED_IN/INITIAL_SESSION) e o getSession() podem
  // disparar a hidratação quase ao mesmo tempo → 2x as queries (users_profile/empresas/user_roles).
  if (_hydratedUserId === user.id) return;
  if (_hydrateInflight) return _hydrateInflight;
  _hydrateInflight = _doHydrateFromUser(user);
  try { await _hydrateInflight; _hydratedUserId = user.id; } finally { _hydrateInflight = null; }
}

async function _doHydrateFromUser(user) {
  // 1) profile
  const { data: profile, error: errP } = await sb
    .from('users_profile')
    .select('id, nome, email, tipo_usuario, empresa_id, avatar_url, must_change_password, mfa_enrolled, onboarding_visto')
    .eq('id', user.id)
    .single();

  if (errP || !profile) {
    console.error('[authContext] users_profile não encontrado:', errP);
    // Sem profile → tenant fica como super_admin "ghost" (empresa_id=null)
    tenant.hydrate({ user, profile: null, config: null });
    return;
  }

  // 2) config da empresa (null se super_admin sem empresa)
  let config = null;
  if (profile.empresa_id) {
    const { data, error: errE } = await sb
      .from('empresas')
      .select('*')
      .eq('id', profile.empresa_id)
      .single();
    if (!errE) config = data;
  }

  // 3) papéis RBAC canônicos (user_roles N:N — feature 002) — fallback mapeado do legado
  let roles = [];
  if (profile.empresa_id) {
    try {
      const { data: ur } = await sb.from('user_roles').select('role')
        .eq('user_id', user.id).eq('tenant_id', profile.empresa_id);
      roles = (ur || []).map((r) => r.role);
    } catch (_) { /* fallback abaixo cobre */ }
  }
  if (!roles.length) {
    const r = ({ administrador: 'tenant_admin', supervisor: 'supervisor_obras', funcionario: 'funcionario_campo', cliente: 'cliente_final', super_admin: 'tenant_admin' })[profile.tipo_usuario] || null;
    if (r) roles = [r];
  }
  // papel singular (compat) = maior precedência
  const PRECEDENCIA = ['tenant_admin', 'gestor_comercial_financeiro', 'supervisor_obras', 'funcionario_rh', 'funcionario_campo', 'cliente_final'];
  const role = PRECEDENCIA.find((r) => roles.includes(r)) || null;

  tenant.hydrate({ user, profile, config, role, roles });
}
